import {
  Box,
  CSSObject,
  IconButton,
  Stack,
  styled,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import 'animate.css';
import MuiDrawer from '@mui/material/Drawer';
import { useEffect, Dispatch, SetStateAction, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { CompanyApprovalStatus, CompanyRole } from 'tdc-web-backend/enums/enums';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { MenuItem } from './MenuItem';
import DashboardAppbar from './appbar/DashboardAppbar';
import Button from '../../components/button/Button';
import { DRAWER_WIDTH_OPEN, DRAWER_WIDTH_CLOSED } from '../../views/Dashboard/Main';
import { primaryDark, primaryLight, secondaryBlue } from '../../utils/color';
import useAuth from '../../utils/hooks/useAuth';
import useMode from '../../utils/hooks/useMode';
import { EnumMode } from '../../utils/context/Mode.context';
import InfoCompleteProfileModal from './components/InfoCompleteProfileModel';
import CustomTooltip from '../../components/tooltip/CustomTooltip';
import useUserCompany from '../../utils/hooks/useUserCompany';
import { ReactComponent as InfoAlertIcon } from '../../assets/icons/layout-icons/InfoAlertIcon.svg';

export interface NavigationDashboardProps {
  items: Array<MenuItem>;
  sx?: SxProps<Theme> | undefined;
  isAdmin?: boolean;
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
  showSidebar?: boolean;
}

export const APPBAR_HEIGHT = 90;
const sidebarBoxShadow = '6px 6px 15px rgba(56, 69, 114, 0.1)';

const openedMixin = (theme: Theme, shouldExtend?: boolean): CSSObject => ({
  width: DRAWER_WIDTH_OPEN,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  outline: 0,
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: DRAWER_WIDTH_CLOSED,
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  height: APPBAR_HEIGHT,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: DRAWER_WIDTH_OPEN,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    borderRight: '1px solid #DCDCFF',
    marginTop: 20,
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme, true),
      '& .MuiDrawer-paper': openedMixin(theme, false),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const NavigationDashboard = ({
  sx,
  isAdmin,
  items,
  isSidebarOpen,
  setIsSidebarOpen,
}: NavigationDashboardProps) => {
  const theme = useTheme();
  const isLessThanLarge = useMediaQuery(theme.breakpoints.down('lg'));
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { mode } = useMode();
  const { logout } = useAuth();
  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);
  const { company: userCompany } = useUserCompany();
  const { t } = useTranslation();

  useEffect(() => {
    setIsSidebarOpen(!isLessThanLarge);
  }, [isLessThanLarge]);

  const findActiveMenuLink = (name: string) => {
    if (name === 'home' && (pathname === '/dashboard/' || pathname === '/dashboard')) return true;
    return pathname.split('/').includes(name.toLowerCase());
  };
  const onCloseTransitionStyle = 'width 0.5s, padding 0.5s, all 0.2s ease';

  const showMode = pathname.startsWith('/dashboard') && !pathname.includes('/create-company');

  const handleSignOut = () => {
    logout();
    navigate('../');
  };

  const getMenuItemColor = (name: string, disabled: boolean | undefined) => {
    if (mode === EnumMode.Supplier && pathname.startsWith('/dashboard')) {
      if (disabled) {
        return primaryDark[300];
      }
      return '#FFF';
    }
    if (findActiveMenuLink(name)) return primaryLight[500];
    return primaryDark[500];
  };

  const getMenuItemBgColor = (name: string) => {
    if (mode === EnumMode.Supplier && pathname.startsWith('/dashboard')) {
      if (findActiveMenuLink(name)) return primaryLight[600];
      return 'transparent';
    }
    if (findActiveMenuLink(name)) return primaryLight[50];
    return 'transparent';
  };

  const getMenuItemHoverColor = (name: string) => {
    if (mode === EnumMode.Supplier && pathname.startsWith('/dashboard')) {
      return primaryLight[700];
    }
    if (findActiveMenuLink(name)) return primaryLight[50];
    return 'transparent';
  };

  const menuLinkStyle = {
    background: 'none',
    gap: '10px',
    justifyContent: isSidebarOpen ? 'start' : 'center',
    fontSize: '14px',
    height: '41px',
    alignItems: 'center',
    borderRadius: 1,
    transition: onCloseTransitionStyle,
    mx: isSidebarOpen ? '16px !important' : '0px !important',
    minWidth: 0,
    pt: '10px',
    pb: '10px',
    pl: isSidebarOpen ? '10px' : '8px',
    '&.Mui-disabled': {
      color: primaryDark[300],
    },
  };
  const drawerBgColor =
    mode === EnumMode.Supplier && pathname.startsWith('/dashboard') ? primaryLight[500] : '#FFF';
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          zIndex: 1,
          height: '100vh',
          ...sx,
        }}
      >
        <DashboardAppbar
          isOpen={isSidebarOpen}
          setIsOpen={setIsSidebarOpen}
          drawerWidth={DRAWER_WIDTH_OPEN}
          appbarHeight={APPBAR_HEIGHT}
        />
        <Drawer
          variant="permanent"
          open={isSidebarOpen}
          sx={{
            display: { xs: 'none', md: isSidebarOpen ? 'block' : 'none', bg: 'block' },
            position: { md: 'fixed', bg: 'fixed', lg: 'relative' },
            '.MuiDrawer-paperAnchorLeft': {
              boxShadow: {
                md: sidebarBoxShadow,
                bg: isSidebarOpen ? sidebarBoxShadow : 'none',
                lg: 'none',
              },
              overflow: 'visible',
              backgroundColor: drawerBgColor,
            },
          }}
        >
          <DrawerHeader
            sx={{
              '&.MuiPaper-root': {
                p: 0,
                m: 0,
              },
              '&.MuiDrawer-paper': { borderRight: 0 },
            }}
          />
          <Stack justifyContent="space-between" height="calc(100% - 90px)" sx={{ pt: 3 }}>
            <Stack spacing={isAdmin ? 1.5 : 3} alignItems={isSidebarOpen ? 'none' : 'center'}>
              {items?.map(({ Icon, ...menu }: MenuItem) => (
                <CustomTooltip
                  key={`tooltip-${menu.id}`}
                  title={menu.toolTip ? t(menu.toolTip) : ''}
                  placement="right"
                >
                  <Stack
                    // title={menu.toolTip ? menu.toolTip : ''}
                    key={`nav-item-${menu.id}`}
                    direction="row"
                    alignItems="center"
                    spacing={0}
                    sx={{ width: '100%' }}
                    justifyContent={isSidebarOpen ? 'space-between' : 'flex-start'}
                  >
                    <Button
                      onButtonClick={() => null}
                      linkTo={menu?.to}
                      setUpperCase
                      disabled={menu.disabled}
                      fontWeight="600"
                      color={getMenuItemColor(menu?.name, menu.disabled)}
                      sx={{
                        ...menuLinkStyle,
                        backgroundColor: getMenuItemBgColor(menu?.name),
                        '&:hover': {
                          backgroundColor: getMenuItemHoverColor(menu?.name),
                        },
                        width: menu.disabled ? 'none' : '100%',
                      }}
                    >
                      <Icon fill={getMenuItemColor(menu?.name, menu.disabled)} />
                      {isSidebarOpen && t(menu.title)}
                    </Button>
                    {menu.disabled && ['case-studies', 'pitches'].includes(menu.name) && (
                      <>
                        {!userCompany?.roles.includes(CompanyRole.SellerService) && (
                          <IconButton
                            onClick={() => setIsInfoModalOpen(true)}
                            sx={{
                              width: '18px',
                              height: '18px',
                              marginRight: isSidebarOpen ? '10px' : 0,
                            }}
                          >
                            <LockIcon
                              fontSize="small"
                              sx={{ color: mode === EnumMode.Client ? 'none' : '#fff' }}
                            />
                          </IconButton>
                        )}
                        {userCompany?.roles.includes(CompanyRole.SellerService) &&
                          userCompany?.status === CompanyApprovalStatus.Pending && (
                            <InfoAlertIcon
                              style={{
                                width: '18px',
                                height: '18px',
                                marginRight: isSidebarOpen ? '13px' : 0,
                              }}
                              fill="#fff"
                              // style={{ width: 22, height: 22 }}
                            />
                          )}
                      </>
                    )}
                  </Stack>
                </CustomTooltip>
              ))}
            </Stack>
            {showMode && (
              <Stack
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{
                  height: '70px',
                  borderColor:
                    mode === EnumMode.Client
                      ? `${secondaryBlue[100]} !important`
                      : `${primaryLight[400]} !important`,
                  borderTop: '1px solid',
                  padding: isSidebarOpen ? '0 24px' : '0px',
                }}
              >
                <Typography
                  variant="bodyMedium3"
                  sx={{
                    color: 'black',
                    backgroundColor: secondaryBlue[100],
                    borderRadius: isSidebarOpen ? '10px' : '50%',
                    padding: isSidebarOpen ? '4px 8px' : '4px 9px',
                    width: isSidebarOpen ? '100%' : 'auto',
                    textAlign: 'center',
                  }}
                >
                  {mode === EnumMode.Client
                    ? isSidebarOpen
                      ? t('common.clientMode')
                      : t('common.cMode')
                    : isSidebarOpen
                    ? t('common.supplierMode')
                    : t('common.sMode')}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Drawer>
      </Box>

      <InfoCompleteProfileModal
        isOpen={isInfoModalOpen}
        setIsOpen={setIsInfoModalOpen}
        onConfirm={() => navigate('../dashboard/unlock-hiring')}
      />
    </>
  );
};

export default NavigationDashboard;
